import { useCallback, useState } from 'react';
import { FlatList, RefreshControl, Text, View } from 'react-native';
import { useFocusEffect } from 'expo-router';
import { useAuth } from '../context/AuthContext';
import { approveWithdrawal, listPendingWithdrawals, rejectWithdrawal, WithdrawalRequest } from '../api/admin';
import { Button, Card, ConfirmDialog, EmptyState, ErrorText, FormInput, Label, ScreenContainer } from '../components/ui';
import { colors, fonts } from '../theme';
import { ApiError } from '../api/client';

export default function AdminWithdrawalsScreen() {
  const { token } = useAuth();
  const [requests, setRequests] = useState<WithdrawalRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<{ id: string; action: 'approve' | 'reject' } | null>(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      setRequests(await listPendingWithdrawals(token));
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Không tải được danh sách yêu cầu rút tiền, vui lòng thử lại.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useFocusEffect(
    useCallback(() => {
      load();
    }, [load]),
  );

  const onApprove = (id: string) => setPendingAction({ id, action: 'approve' });
  const onReject = (id: string) => setPendingAction({ id, action: 'reject' });

  const confirmPendingAction = async () => {
    if (!token || !pendingAction) return;
    const { id, action } = pendingAction;
    setBusyId(id);
    setError(null);
    try {
      if (action === 'approve') {
        await approveWithdrawal(token, id);
      } else {
        await rejectWithdrawal(token, id, reasons[id]);
      }
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : action === 'approve' ? 'Duyệt thất bại' : 'Từ chối thất bại');
    } finally {
      setBusyId(null);
      setPendingAction(null);
    }
  };

  const pendingRequest = pendingAction ? requests.find((r) => r.id === pendingAction.id) : null;

  return (
    <ScreenContainer>
      <ErrorText>{error}</ErrorText>
      <FlatList
        data={requests}
        keyExtractor={(item) => item.id}
        refreshControl={<RefreshControl refreshing={loading} onRefresh={load} />}
        ListEmptyComponent={!loading && !error ? <EmptyState text="Không có yêu cầu rút tiền nào đang chờ" /> : null}
        renderItem={({ item }) => (
          <Card>
            <Text style={{ fontSize: 16, fontFamily: fonts.bold, color: colors.text }}>
              {item.user?.name ?? item.userId}
            </Text>
            <Text style={{ color: colors.textMuted, marginTop: 2 }}>{item.user?.email}</Text>
            <Text style={{ color: colors.text, marginTop: 8, fontSize: 15, fontFamily: fonts.semiBold }}>
              Rút {Math.abs(item.amountCoins)} xu
            </Text>
            <Text style={{ color: colors.textMuted, marginTop: 4, fontSize: 12 }}>{item.note}</Text>
            <Text style={{ color: colors.textMuted, marginTop: 8, fontSize: 13 }}>
              {item.payoutBankNameSnapshot ?? '—'} · {item.payoutAccountNumberSnapshot ?? '—'} ·{' '}
              {item.payoutAccountHolderNameSnapshot ?? '—'}
            </Text>
            <Text style={{ color: colors.textMuted, marginTop: 4, fontSize: 11 }}>
              {new Date(item.createdAt).toLocaleString('vi-VN')}
            </Text>

            <View style={{ marginTop: 12 }}>
              <Label>Lý do từ chối (nếu từ chối)</Label>
              <FormInput
                value={reasons[item.id] ?? ''}
                onChangeText={(v) => setReasons((r) => ({ ...r, [item.id]: v }))}
                placeholder="Ví dụ: số tài khoản không khớp với CCCD"
              />
              <Button
                title="Đã chuyển khoản — Duyệt"
                onPress={() => onApprove(item.id)}
                loading={busyId === item.id}
              />
              <Button
                title="Từ chối và hoàn xu"
                variant="danger"
                onPress={() => onReject(item.id)}
                loading={busyId === item.id}
              />
            </View>
          </Card>
        )}
      />
      <ConfirmDialog
        visible={pendingAction !== null}
        title={pendingAction?.action === 'approve' ? 'Xác nhận duyệt rút tiền' : 'Xác nhận từ chối rút tiền'}
        message={
          pendingRequest
            ? pendingAction?.action === 'approve'
              ? `Xác nhận đã chuyển khoản ${Math.abs(pendingRequest.amountCoins)} xu cho "${pendingRequest.user?.name ?? pendingRequest.userId}"? Thao tác này không thể hoàn tác.`
              : `Từ chối yêu cầu rút ${Math.abs(pendingRequest.amountCoins)} xu của "${pendingRequest.user?.name ?? pendingRequest.userId}" và hoàn xu lại cho họ?`
            : ''
        }
        confirmLabel={pendingAction?.action === 'approve' ? 'Duyệt' : 'Từ chối'}
        danger={pendingAction?.action === 'reject'}
        loading={busyId !== null}
        onConfirm={confirmPendingAction}
        onCancel={() => setPendingAction(null)}
      />
    </ScreenContainer>
  );
}
